/**
 * Pricing — two one-time tiers, no subscriptions.
 * Quick Theme $49 · Custom Theme $149
 */

import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    description: 'A polished, ready-to-install theme with your branding applied',
    badge: null,
    badgeColor: 'success',
    icon: <BoltIcon />,
    iconColor: 'success.main',
    highlight: false,
    route: '/quick-themes',
    features: [
      '1 Quick Theme — template-based',
      '10 pages (7 complete + 3 blank templates)',
      '6 visual styles: Modern, Classic, Bold & more',
      '12 color schemes per style',
      '6 pre-paired font combinations',
      'Responsive + SEO optimized',
      'Pay only if generation succeeds',
    ],
    buttonText: 'Get Starter',
    buttonVariant: 'outlined',
    buttonColor: 'success',
  },
  {
    id: 'professional',
    name: 'Custom',
    price: 149,
    description: 'A theme shaped around your industry, pages, and content',
    badge: 'Most Popular',
    badgeColor: 'primary',
    icon: <AutoAwesomeIcon />,
    iconColor: 'primary.main',
    highlight: true,
    route: '/ai-custom',
    features: [
      '1 Custom Theme — fully generated',
      'Up to 70 industry-specific pages',
      'Custom content for your business',
      'Industry-appropriate features & layouts',
      'Auto page creation on activation',
      'Responsive + SEO optimized',
      'Pay only if generation succeeds',
    ],
    buttonText: 'Get Custom Theme',
    buttonVariant: 'contained',
    buttonColor: 'primary',
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error, setError]   = useState('');

  const handleChoosePlan = async (plan) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    setLoading(plan.id);
    setError('');

    // Quick and Custom — go straight to the theme builder; payment happens after generation
    navigate(plan.route);
    setLoading(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          One price. Your theme. Keep it forever.
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No subscriptions, no recurring fees — just a theme that fits your business.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No subscriptions. No recurring fees. Pay once, own your theme forever.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      )}
      {!isAuthenticated && (
        <Alert severity="info" sx={{ mb: 4 }}>
          Sign in or create a free account to purchase
        </Alert>
      )}

      {/* Plan cards */}
      <Grid container spacing={4} alignItems="stretch" justifyContent="center">
        {plans.map((plan) => (
          <Grid item xs={12} md={5} key={plan.id}>
            <Card
              elevation={plan.highlight ? 8 : 3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: plan.highlight ? '2px solid' : '1px solid',
                borderColor: plan.highlight ? 'primary.main' : 'divider',
                transform: plan.highlight ? 'scale(1.03)' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              {plan.badge && (
                <Chip
                  label={plan.badge}
                  color={plan.badgeColor}
                  size="small"
                  sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 600 }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, pt: plan.badge ? 5 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ mr: 1, color: plan.iconColor }}>{plan.icon}</Box>
                  <Typography variant="h5" component="h2">{plan.name}</Typography>
                </Box>

                <Box sx={{ my: 3 }}>
                  <Typography variant="h3" component="div" color="primary">
                    ${plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    one-time
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {plan.description}
                </Typography>

                <List dense>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant={plan.buttonVariant}
                  color={plan.buttonColor}
                  size="large"
                  onClick={() => handleChoosePlan(plan)}
                  disabled={loading === plan.id}
                >
                  {loading === plan.id
                    ? <CircularProgress size={24} />
                    : plan.buttonText}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pay-on-completion guarantee */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.light' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          <strong>Pay-on-Completion Guarantee:</strong> You're only charged when your theme is ready to download. Nothing works? Nothing due.
        </Typography>
      </Box>

      {/* Comparison */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Which one is right for you?
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="success.main">
              Quick Theme ($49)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose from pre-designed templates, pick your layout and colors, and get a polished
              7-page theme in 5–10 minutes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Best for:</strong> Portfolios, landing pages, small business sites, quick turnarounds
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom color="primary.main">
              Custom Theme ($149 / $299 for 3)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Built for your specific industry. Select from up to 70 industry-specific pages, get
              content written for your business, and industry-appropriate features. Ready in 15–20 minutes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Best for:</strong> Professional sites, industry-specific needs, feature-rich websites, client projects
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* FAQ */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              What is pay-on-completion?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're only charged if your theme generates successfully and passes validation.
              If generation fails, you pay $0 and get free retries.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Can I use the theme on multiple sites?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Yes — each theme you purchase is yours to use and customize on any site you own or build for clients.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              What happens if generation fails?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No charge. You get up to 3 free retries. If all attempts fail, contact support and we'll help you manually.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Can I upgrade from Starter to Custom?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Yes. Purchase the Custom tier and generate a new Custom theme. Your Starter theme remains yours.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Do you offer refunds?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We only charge for successful generations, so refunds are rarely needed. Contact support within 7 days
              if you have an issue with a delivered theme.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Contact */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Need a custom solution?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Contact us for volume pricing or custom requirements
        </Typography>
        <Button variant="outlined" size="large">
          Contact Sales
        </Button>
      </Box>

    </Container>
  );
};

export default Pricing;
